import { useEffect, useState } from 'react';
import { getAvatarDisplay } from '@wineryxplorer/shared';
import type { SessionUser } from '@wineryxplorer/shared';

interface AvatarProps {
  user: SessionUser | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Avatar = ({ user, size = 'md', className = '' }: AvatarProps) => {
  const avatarDisplay = getAvatarDisplay(user);
  const currentUrl = avatarDisplay.type === 'image' ? avatarDisplay.url : null;
  const [imgError, setImgError] = useState(false);

  // Reset error state when the avatar URL changes so a new avatar can load
  // even if a previous one failed (e.g., transient Cloudinary glitch right
  // after onboarding upload).
  useEffect(() => {
    setImgError(false);
  }, [currentUrl]);

  // Size classes for the avatar container
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  const containerClasses = `${sizeClasses[size]} rounded-full flex items-center justify-center ${className}`;

  if (avatarDisplay.type === 'image' && !imgError) {
    return (
      <img
        src={avatarDisplay.url}
        alt={user?.name || 'User avatar'}
        className={`${containerClasses} object-cover`}
        onError={() => setImgError(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  // Fallback to initial if we have a name (also used when image fails to load)
  if (user?.name && user.name.trim().length > 0) {
    const firstLetter = user.name.trim()[0].toUpperCase();
    return (
      <div className={`${containerClasses} bg-primary-600 text-white font-semibold`}>
        {firstLetter}
      </div>
    );
  }

  if (avatarDisplay.type === 'initial') {
    return (
      <div className={`${containerClasses} bg-primary-600 text-white font-semibold`}>
        {avatarDisplay.letter}
      </div>
    );
  }

  // Fallback: generic user icon
  return (
    <div className={`${containerClasses} bg-gray-300 text-gray-600`}>
      <svg className="w-2/3 h-2/3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    </div>
  );
};
